import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import { DollarSign, TrendingUp, History, CheckCircle2 } from 'lucide-react';

type HistoricalTrade = {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  lot: number;
  openPrice: number;
  closePrice: number;
  profit: number;
  commission: number;
  openTime: string;
  closeTime: string;
  strategyName: string;
  status: 'CLOSED' | 'CANCELLED';
};

const MOCK_HISTORY: HistoricalTrade[] = [
  {
    id: 'HT001',
    symbol: 'XAUUSD',
    type: 'BUY',
    lot: 0.5,
    openPrice: 2020.0,
    closePrice: 2035.5,
    profit: 775.0,
    commission: 5.0,
    openTime: '2024-02-01 09:00',
    closeTime: '2024-02-01 14:30',
    strategyName: 'Gold Scalper Pro',
    status: 'CLOSED',
  },
  {
    id: 'HT002',
    symbol: 'EURUSD',
    type: 'SELL',
    lot: 1.0,
    openPrice: 1.082,
    closePrice: 1.077,
    profit: 500.0,
    commission: 7.0,
    openTime: '2024-02-02 11:15',
    closeTime: '2024-02-02 17:45',
    strategyName: 'Forex Trend Master',
    status: 'CLOSED',
  },
  {
    id: 'HT003',
    symbol: 'GBPUSD',
    type: 'BUY',
    lot: 0.5,
    openPrice: 1.265,
    closePrice: 1.261,
    profit: -200.0,
    commission: 5.0,
    openTime: '2024-02-03 10:00',
    closeTime: '2024-02-03 12:30',
    strategyName: 'Conservative Growth',
    status: 'CLOSED',
  },
];

const CopyTradingHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStrategy, setSelectedStrategy] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('');

  const totalClosedProfit = MOCK_HISTORY.reduce(
    (acc, curr) => acc + curr.profit,
    0
  );
  const winCount = MOCK_HISTORY.filter(t => t.profit > 0).length;
  const winRate = ((winCount / MOCK_HISTORY.length) * 100).toFixed(1);

  const columns: DataTableColumn<HistoricalTrade>[] = [
    {
      id: 'id',
      header: 'Trade ID',
      accessor: r => r.id,
      sortable: true,
      width: '110px',
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: r => <span className="font-semibold">{r.symbol}</span>,
      sortable: true,
      width: '110px',
    },
    {
      id: 'strategyName',
      header: 'Strategy',
      accessor: r => r.strategyName,
      sortable: true,
      width: '170px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: r => (
        <Badge
          className={
            r.type === 'BUY'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }
        >
          {r.type}
        </Badge>
      ),
      sortable: true,
      width: '100px',
    },
    {
      id: 'lot',
      header: 'Lot Size',
      accessor: r => r.lot,
      sortable: true,
      width: '100px',
    },
    {
      id: 'openPrice',
      header: 'Open Price',
      accessor: r => r.openPrice,
      sortable: true,
      width: '120px',
    },
    {
      id: 'closePrice',
      header: 'Close Price',
      accessor: r => r.closePrice,
      sortable: true,
      width: '120px',
    },
    {
      id: 'profit',
      header: 'Realized Profit ($)',
      accessor: r => (
        <span
          className={
            r.profit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${r.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
      width: '150px',
    },
    {
      id: 'closeTime',
      header: 'Close Time',
      accessor: r => r.closeTime,
      sortable: true,
      width: '150px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: r => (
        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
          {r.status}
        </span>
      ),
      sortable: true,
      width: '110px',
    },
  ];

  const filteredTrades = MOCK_HISTORY.filter(t => {
    const matchesSearch =
      searchTerm === '' ||
      t.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.strategyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStrategy =
      selectedStrategy === 'all' || t.strategyName === selectedStrategy;

    const matchesStatus =
      selectedStatus === 'all' || t.status === selectedStatus;

    const matchesDate = !dateFilter || t.closeTime.includes(dateFilter);

    return matchesSearch && matchesStrategy && matchesStatus && matchesDate;
  });

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Trade History"
          description="Historical record of all closed copied orders and historical performance metrics"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Closed Trades"
          value={MOCK_HISTORY.length.toString()}
          change="Completed orders"
          icon={History}
        />
        <StatsCard
          title="Realized Profit"
          value={`+$${totalClosedProfit.toFixed(2)}`}
          change="Closed earnings"
          icon={TrendingUp}
        />
        <StatsCard
          title="Win Rate"
          value={`${winRate}%`}
          change={`${winCount} winning trades`}
          icon={CheckCircle2}
        />
        <StatsCard
          title="Total Paid Fees"
          value="$17.00"
          change="Performance fees"
          icon={DollarSign}
        />
      </div>

      {/* Filter Bar */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Execution History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Strategy Filter</Label>
              <Select
                value={selectedStrategy}
                onValueChange={setSelectedStrategy}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Strategies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Strategies</SelectItem>
                  <SelectItem value="Gold Scalper Pro">
                    Gold Scalper Pro
                  </SelectItem>
                  <SelectItem value="Forex Trend Master">
                    Forex Trend Master
                  </SelectItem>
                  <SelectItem value="Conservative Growth">
                    Conservative Growth
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status Filter</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="CLOSED">CLOSED</SelectItem>
                  <SelectItem value="CANCELLED">CANCELLED</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateFilter">Date Filter</Label>
              <Input
                id="dateFilter"
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Closed Trade History</CardTitle>
          <CardDescription>
            All closed copied transactions with entry and exit details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredTrades}
            searchPlaceholder="Search by trade ID, symbol, or strategy..."
            onSearchChange={setSearchTerm}
            showAddButton={false}
            showExportButton={false}
            showColumnVisibility={true}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CopyTradingHistory;
